import Layers from "slotworks/graphics/layers/layers";
import { Signal } from "signals";
import Timer from "slotworks/utils/timer";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Contract from "slotworks/utils/contract";
import * as TweenJS from "slotworks/utils/tween";
import soundManager from "slotworks/sound/sound-manager";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import Rectangle from "slotworks/utils/geom/rectangle";
import currency from "slotworks/i18n/currency";
import deviceInfo from "slotworks/utils/device-info";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import gameState from "slotworks/model/game-state";
import BonusRecord from "integration/revolver/schema/bonus-record";

// todo refactor 
export default class Wheel {

    public static SPACE_BETWEEN: number = 30;

    public static MIN_REPEATS: number = 2;

    public static SPEED: number = 2;

    public static MIN_SPEED: number = 0.2;

    public static START_SPEED: number = 1;

    public static DECELERATE_SPEED = -0.0013;

    public static DECELERATE_MIDDLE_SPEED = -0.0002;

    public static ACCELERATE_SPEED = 0.0005;

    public static VERTICES_ALONG_Y = 100;

    public static MIN_SPEED_MOTION = -0.04; // fix for bigger time interval between frames, cant use lerp    

    public onComplete: Signal = new Signal();
    public onReady: Signal = new Signal();

    private plaques: Plaque[];
    private glows: PIXI.Sprite[];

    private winKey: number;
    private winIndex: number;

    private top: number;
    private bottom: number;

    private lastTime: number;

    private tickTimer: number;

    private repeats: number;

    private winPosition: number;
    private distanceToWin: number;

    private calculated: boolean;

    private speed: number;
    private speedMotion: number;

    private renderTexture: PIXI.RenderTexture;
    private renderContainer: PIXI.Container;
    private renderPlane: PIXI.mesh.Plane;
    private indexInParent: number;

    private topY: number;
    private bottomY: number;
    private scaleDifference: number;

    private totalDistance: number;

    private wheelContainer: PIXI.Container;

    private bonusContainerY: number;

    // moving the wheel in texture
    // need to keep texture updating after because of glow and pot tweens
    private animating: boolean;

    private static KEYS: number[] = [10, 20, 30, 50, 80, 100, 200, 500, 2000];

    private static GLOWS: string[] = [
        "wheel_glow_10_20_30_50x",
        "wheel_glow_10_20_30_50x",
        "wheel_glow_10_20_30_50x",
        "wheel_glow_10_20_30_50x",
        "wheel_glow_80x",
        "wheel_glow_100x",
        "wheel_glow_200x",
        "wheel_glow_500x",
        "wheel_glow_3000x"
    ];

    public jackpotAnim: AnimatedSprite;

    constructor(multiplier: number, jackpot: number) {
        this.plaques = [];
        for (let key of Wheel.KEYS) {
            let name: string;
            // things will eventually change so I wont change resource names for now
            if (key === 2000)
                name = `wheel_wedge_3000x`;
            else if (multiplier > 1)
                name = `wheel_wedge_${multiplier}x_${key * multiplier}x`;
            else
                name = `wheel_wedge_${key}x`;
            const item = Layers.Bonus.getSprite(name);
            this.plaques.push(new Plaque(key, item));
            item.visible = false;
        }
        this.hideGlows();
        this.setToPositions();
        this.makeJackpotWage(jackpot);
        this.init();
        this.show().then(() => this.onReady.dispatch());
    }

    private init() {
        const background = Layers.Bonus.getSprite("wheel_strip_bg");
        const dark_top = Layers.Bonus.getSprite("wheel_darkgrad");
        const dark_bottom = Layers.Bonus.getSprite("wheel_darkgrad_bottom");
        const backgroundTransform = new Rectangle(background.x, background.y, background.width, background.height);
        background.scale.y += 0.3;
        this.scaleDifference = (background.height - backgroundTransform.height) / 2;
        background.y -= this.scaleDifference;
        dark_top.y -= this.scaleDifference;
        dark_bottom.y += this.scaleDifference;

        // FIX: Create larger texture to accommodate bezier distortion
        const textureWidth = background.width * 1.5; // Increase to fit bezier curve expansion
        const textureHeight = background.height;

        this.renderTexture = PIXI.RenderTexture.create(textureWidth, textureHeight);
        this.renderContainer = new PIXI.Container();
        this.renderPlane = new PIXI.mesh.Plane(this.renderTexture, 2, Wheel.VERTICES_ALONG_Y);
        this.renderPlane.x = 0;
        this.renderPlane.y = (1080 - this.renderPlane.height) / 2;

        const parent = Layers.Bonus.container.parent;
        this.indexInParent = parent.getChildIndex(Layers.Bonus.container)
        Layers.Bonus.container.x = 0;
        this.renderContainer.addChild(Layers.Bonus.container);

        Layers.Bonus.container.y = -background.y;
        this.bonusContainerY = background.height;

        this.wheelContainer = new PIXI.Container();
        this.wheelContainer.addChild(this.renderPlane);
        parent.addChildAt(this.wheelContainer, this.indexInParent);

        this.bonusContainerY = -background.y;
        canvasManager.renderer.render(this.renderContainer, this.renderTexture);

        const points = this.bezier(-80, 0, 100, canvasManager.stageHeight / 2, -80, canvasManager.stageHeight, Wheel.VERTICES_ALONG_Y);
        this.reshape(points);

        const scale = canvasManager.stageHeight / this.renderPlane.height;
        this.wheelContainer.scale.set(scale, scale);

        this.wheelContainer.x = (canvasManager.stageWidth - this.wheelContainer.width) / 2 + this.wheelContainer.parent.x;
        if (!deviceInfo.isDesktop) {
            canvasManager.onOrientationChange.add(this.onOrientationChange, this);
            this.onOrientationChange();
        }
    }

    public onOrientationChange() {
        Layers.Bonus.container.x = 0;
        Layers.Bonus.container.y = this.bonusContainerY;
        const scale = canvasManager.stageHeight / this.renderPlane.height;
        const width = Layers.Bonus.container.width * scale;
        this.wheelContainer.scale.set(scale, scale);
        if (canvasManager.orientation == Orientation.PORTRAIT) {
            if (deviceInfo.layoutMain == "mobile") {
                this.wheelContainer.x = (canvasManager.stageWidth - width) / 2 - this.wheelContainer.parent.x;
            } else
                this.wheelContainer.x = (canvasManager.stageWidth - this.wheelContainer.width) / 2 - this.wheelContainer.parent.x;
        } else {
            this.wheelContainer.x = (canvasManager.stageWidth - this.wheelContainer.width) / 2 - this.wheelContainer.parent.x;
        }
        this.wheelContainer.y = this.wheelContainer.parent.toLocal(new PIXI.Point(0, 0)).y;
    }

    private show(): Contract<void> {
        return new Contract(resolve => {
            const height = this.renderPlane.height;
            const target = this.renderPlane.y;
            this.renderPlane.y -= height;
            new TweenJS.Tween(this.renderPlane)
                .to({ y: target }, 500)
                .onComplete(() => resolve(null))
                .start();
        });
    }

    private easeIn(t: number, b: number, c: number, d: number) {
        t /= d;
        return c * t * t + b;
    }

    private easeOut(t: number, b: number, c: number, d: number) {
        t /= d;
        return -c * t * (t - 2) + b;
    }

    private reshape(points: number[]) {
        const vertices = this.renderPlane.vertices;
        for (let i = 0; i < points.length; i++) {
            vertices[i * 4] -= points[i];
            vertices[i * 4 + 2] += points[i];
        }
        const borderTop = Wheel.VERTICES_ALONG_Y / 4;
        const startCoordinate = 0 + this.scaleDifference; // screenStart
        const endVertexPos = vertices[(borderTop + 1) * 4 + 1] - startCoordinate;
        for (let i = 0; i <= borderTop; i++) {
            // left
            vertices[i * 4 + 1] = this.easeIn(i, startCoordinate, endVertexPos, borderTop + 1);
            // right
            vertices[i * 4 + 3] = this.easeIn(i, startCoordinate, endVertexPos, borderTop + 1);
        }
        const borderBottom = Math.floor(Wheel.VERTICES_ALONG_Y * 3 / 4);
        const totalVerticesBottom = Wheel.VERTICES_ALONG_Y - borderBottom;
        const outStartCoordinate = vertices[(borderBottom) * 4 + 1];
        const outEndVertexPos = vertices[(Wheel.VERTICES_ALONG_Y - 1) * 4 + 1] - this.scaleDifference - outStartCoordinate;
        for (let i = borderBottom; i < Wheel.VERTICES_ALONG_Y; i++) {
            // left
            vertices[i * 4 + 1] = this.easeOut(i - borderBottom, outStartCoordinate, outEndVertexPos, totalVerticesBottom);
            // right
            vertices[i * 4 + 3] = this.easeOut(i - borderBottom, outStartCoordinate, outEndVertexPos, totalVerticesBottom);
        }
    }

    private reshape2(p: number[][]) {
        const points = p[0];
        const py = p[1];
        const vertices = this.renderPlane.vertices;
        for (let i = 0; i < points.length; i++) {
            vertices[i * 4] -= points[i];
            vertices[i * 4 + 2] += points[i];
            vertices[i * 4 + 1] = py[i];
            vertices[i * 4 + 3] = py[i];
        }
    }

    private bezier2(startX: number, startY: number, middleX: number, middleY: number, endX: number, endY: number, steps: number) {
        const points = [];
        const py = [];
        for (let i = 0; i < steps; i++) {
            let t = i / steps;
            let a = Math.pow((1.0 - t), 2.0);
            let b = 2.0 * t * (1.0 - t);
            let c = Math.pow(t, 2.0);
            let x = a * startX + b * middleX + c * endX;
            let y = a * startY + b * middleY + c * endY;
            points[i] = x;
            py[i] = y;
        }
        return [points, py];
    }

    private bezier(startX: number, startY: number, middleX: number, middleY: number, endX: number, endY: number, steps: number) {
        const points = [];
        for (let i = 0; i < steps; i++) {
            let t = i / steps;
            let a = Math.pow((1.0 - t), 2.0);
            let b = 2.0 * t * (1.0 - t);
            let c = Math.pow(t, 2.0);
            let x = a * startX + b * middleX + c * endX;
            points[i] = x;
        }
        return points;
    }

    public start() {
        this.animating = true;
        this.lastTime = Timer.time;
        this.repeats = 0;
        this.calculated = false;
        this.speed = Wheel.START_SPEED;
        this.speedMotion = Wheel.ACCELERATE_SPEED;
        this.startTick();
    }

    private makeJackpotWage(jackpot: number) {

        this.jackpotAnim = Layers.Bonus.getAnimatedSprite("bonus_wheel_jp_ruby");
        this.jackpotAnim.visible = false;
        this.jackpotAnim.stop();
        this.spriteToWedge(this.jackpotAnim);
        const title = Layers.Bonus.getSprite("wheel_jackpot_title");
        this.spriteToWedge(title);
        const bar = Layers.Bonus.getSprite("wheel_jackpot_bar");
        this.spriteToWedge(bar);
        const value = Layers.Bonus.getBitmapText("jackpot");
        value.text = currency.format(jackpot, undefined, undefined, true);
        value.position.y -= 14;
        this.spriteToWedge(value);
    }

    private spriteToWedge(sprite: PIXI.Sprite | PIXI.extras.BitmapText | AnimatedSprite) {
        const jackpotPlaque = this.plaques[this.plaques.length - 1];
        let local: PIXI.Point = jackpotPlaque.element.toLocal(sprite.getGlobalPosition());
        jackpotPlaque.element.addChild(sprite);
        sprite.position.set(local.x, local.y);
    }

    public setWinner(key: number) {
        this.winKey = key;
        this.winIndex = Wheel.KEYS.findIndex(el => el === this.winKey);
        // todo server should send correct data
        if (this.winIndex < 0)
            this.winIndex = this.plaques.length - 1;
    }

    private setToPositions() {
        const background = Layers.Bonus.getSprite("wheel_strip_bg");
        this.topY = background.y - 50; // todo constant and portrait
        this.bottomY = background.y + background.height + 150;
        let top: number = this.plaques.length - 1;
        let last: Plaque = this.plaques[top];
        last.element.visible = true;
        this.winPosition = last.element.y + last.height / 2;
        while (top > 0) {
            top--;
            const plaque = this.plaques[top];
            plaque.element.visible = true;
            plaque.element.y = last.element.y - plaque.element.height - Wheel.SPACE_BETWEEN;
            last = plaque;
            if (plaque.element.y < this.topY)
                break;
        }
        let bottom: number = 0;
        last = this.plaques[this.plaques.length - 1];
        while (bottom < this.plaques.length) {
            const plaque = this.plaques[bottom];
            plaque.element.visible = true;
            plaque.element.y = last.element.y + last.element.height + Wheel.SPACE_BETWEEN;
            last = plaque;
            bottom++;
            // todo
            if (plaque.element.y > this.bottomY) {
                break;
            }
        }
        this.top = top;
        this.bottom = bottom + 1; // safety     
    }

    // fixed to distance linear deceleration
    private update() {
        if (this.animating) {
            let difTime = Timer.time - this.lastTime;
            this.lastTime = Timer.time;

            let dif;
            if (this.calculated) {
                let speedMulti = this.distanceToWin / this.totalDistance;
                if (speedMulti < 0.1) speedMulti = 0.1; // todo                
                dif = difTime * this.speed * speedMulti;
            } else {
                dif = difTime * this.speed;
                if (this.speed > Wheel.SPEED) {
                    this.speed = Wheel.SPEED;
                    this.speedMotion = 0;
                }
                if (this.winKey && this.repeats > Wheel.MIN_REPEATS && !this.calculated && this.winIndex === this.top) {
                    this.calculateDistanceToFinish();
                    this.speedMotion = Wheel.DECELERATE_SPEED;
                } else if (this.winKey && this.repeats === Wheel.MIN_REPEATS) {
                    this.speedMotion = Wheel.DECELERATE_MIDDLE_SPEED;
                }
            }
            this.move(dif);
            if (this.calculated) {
                this.distanceToWin -= dif;
                if (this.distanceToWin <= 0) {
                    if (this.distanceToWin < 0)
                        this.move(this.distanceToWin);
                    this.finish();
                }
            } else {
                let speedDif = difTime * this.speedMotion;
                if (speedDif < Wheel.MIN_SPEED_MOTION)
                    speedDif = Wheel.MIN_SPEED_MOTION;
                this.speed += speedDif;
            }
        }
        canvasManager.renderer.render(this.renderContainer, this.renderTexture);
    }

    private finish() {
        this.animating = false;
        soundManager.customEvent("bonus_wheel_spin_tick");
        if (this.winIndex === this.plaques.length - 1)
            soundManager.customEvent("bonus_wheel_jackpot");
        else if (this.winKey === 10) {
            // soundManager.customEvent("multiplier_award_10x");
            soundManager.customEvent("bonus_wheel_multiplier");
        } else
            soundManager.customEvent("bonus_wheel_multiplier");
        this.glowAnimation().then(() => {
            setTimeout(() => {
                if (this.winIndex === this.plaques.length - 1) {
                    this.jackpotTween().then(() => this.onComplete.dispatch());
                }
                else
                    this.onComplete.dispatch();
                this.stopTick();
            }, 1000);
        });
    }

    private calculateDistanceToFinish() {
        this.calculated = true;
        const plaque = this.plaques[this.winIndex];
        this.distanceToWin = this.winPosition - (plaque.element.y + plaque.height / 2);
        this.totalDistance = this.distanceToWin;
    }

    private move(dif: number) {
        const topPlaque = this.plaques[this.top];
        topPlaque.element.y += dif;
        let index = this.getNextIndex(this.top);
        if (topPlaque.element.y + topPlaque.height > this.topY) {
            this.top = this.getPreviousIndex(this.top);
            const plaque = this.plaques[this.top];
            plaque.element.visible = true;
            plaque.element.y = topPlaque.element.y - plaque.height - Wheel.SPACE_BETWEEN;
            if (this.top === 0) this.repeats++; // not exact but works
            this.bottom = this.getPreviousIndex(this.bottom);
            soundManager.customEvent("bonus_wheel_spin_tick");
        }
        do {
            const plaque = this.plaques[index];
            plaque.element.y += dif;
            if (plaque.element.y > this.bottomY)
                plaque.element.visible = false;
            index = this.getNextIndex(index);
        } while (index != this.bottom);
    }

    private getPreviousIndex(id: number): number {
        return id <= 0 ? this.plaques.length - 1 : id - 1;
    }

    private getNextIndex(id: number): number {
        return id >= this.plaques.length - 1 ? 0 : id + 1;
    }

    private startTick() {
        this.stopTick();
        this.tickTimer = Timer.setInterval(this.tick, 0);
    }

    private stopTick() {
        Timer.clearInterval(this.tickTimer);
    }

    private tick = () => {
        this.update();
    }

    private jackpotTween(): Contract<void> {
        return new Contract<void>(resolve => {
            resolve(null)
        });
    }

    private glowAnimation(): Contract<void> {
        return new Contract(resolve => {
            const glow = this.glows[this.winIndex];
            if (!glow) {
            //    console.warn('Glow element not found');
                resolve();
                return;
            }
            const record = gameState.getCurrentGame().latestRecord();
            let bonusRecord;
            if (record instanceof BonusRecord) {
                bonusRecord = record as BonusRecord;
            } else
                bonusRecord = gameState.getCurrentGame().getPreviousRecord() as BonusRecord;
            if (bonusRecord.isJackpotWon) {
                this.jackpotAnim.loop = true;
                this.jackpotAnim.visible = true;
                this.jackpotAnim.gotoAndPlay(0);
            }

            glow.visible = true;
            glow.alpha = 0;
            new TweenJS.Tween(glow)
                .to({ alpha: 1 }, 50)
                .onComplete(() => { resolve(null) })
                .start();
        });
    }

    private hideGlows() {
        this.glows = [];
        for (let glow of Wheel.GLOWS) {
            const sprite = Layers.Bonus.getSprite(glow);
            sprite.visible = false;
            this.glows.push(sprite);
        }
        // what to do with this
        Layers.Bonus.getSprite("wheel_lightgrad").visible = false;
    }

    public clean(): void {
        canvasManager.onOrientationChange.remove(this.onOrientationChange, this);

        // Set anchor 
        this.wheelContainer.pivot.x = this.wheelContainer.width / 2;
        this.wheelContainer.pivot.y = this.wheelContainer.height / 2;
        this.wheelContainer.x += this.wheelContainer.width / 2;
        this.wheelContainer.y += this.wheelContainer.height / 2;

        // Animate scale from 1 to 0 
        const scaleDuration = 300;
        const startTime = Date.now();

        const animateScale = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / scaleDuration, 1);
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            this.wheelContainer.scale.x = 1 - easeProgress;
            this.wheelContainer.scale.y = 1 - easeProgress;

            if (progress < 1) {
                requestAnimationFrame(animateScale);
            } else {
                // Restore state and free resources
                this.wheelContainer.removeChild(this.renderPlane);
                Layers.Bonus.container.x = this.renderPlane.x;
                this.wheelContainer.parent.addChildAt(Layers.Bonus.container, this.indexInParent);
                this.wheelContainer.parent.removeChild(this.wheelContainer);
                Layers.Bonus.container.removeChildren();
                this.renderPlane.destroy();
                this.renderTexture.destroy();
            }
        };

        animateScale();
    }

}

class Plaque {
    public id: number;
    public element: PIXI.Container;
    public width: number;
    public height: number;

    constructor(id: number, element: PIXI.Container) {
        this.id = id;
        this.element = element;
        this.height = element.height;
        this.width = element.width;
    }

}
